import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useDragControls, useReducedMotion } from "framer-motion";
import { Anchor, ArrowLeftRight, Building2, Coins, Gem, Landmark, ShoppingBag, Swords, Trophy, X } from "lucide-react";
import { PlaceCard } from "@/city3d/Hud";
import { HallPanel } from "@/hud/Hall";
import { EconomyTab, ShopTab } from "./Economy";
import { Leaderboard } from "./Leaderboard";
import { Btn, PANEL, Section, Skel } from "./kit";
import { ACC, useMedia } from "./util";

const TABS = [["shop", "Shop", ShoppingBag], ["economy", "Economy", Coins], ["leaders", "Leaders", Trophy], ["hall", "City Hall", Landmark]];
const Prompt = ({ onSignIn }) => (
  <div className="p-6 text-center" data-testid="drawer-signin" title="Sign in with X to rent berths to ships, collect UXD and rent a house uptown.">
    <Anchor className="mx-auto h-10 w-10 text-x-accent" strokeWidth={1.8} />
    <div className="mt-2 text-lg font-semibold">Run a dock, earn UXD</div>
    <Btn className="mt-4 px-5 py-2.5" onClick={onSignIn}>Sign in with X</Btn>
  </div>
);

const Loading = () => <div className="space-y-3 p-4"><Skel className="h-24" /><Skel className="h-16" /><Skel className="h-32" /></div>;

// The rules as icon tiles; the full sentence is behind (i) and each tile's tooltip.
function How({ econ }) {
  const v = econ?.visitors;
  const tiles = [
    [Anchor, "Ships", "Late = ½", "blue", "Everyone owns a dock. Rent your berths to ships, come back and collect UXD. Late ships pay half."],
    [ArrowLeftRight, "Traders", `+${v?.step ?? 10}%`, "green", `Every trader today boosts your payouts +${v?.step ?? 10}%, up to +${v?.max ?? 100}%.`],
    [Building2, "Uptown", "Weekly rent", "green", "Rent a lot uptown by the week. Lots by downtown cost more. Miss rent and you move back to the Docks; your ships keep working."],
    [Gem, "The Row", "Top 10", "rose", "The top 10 weekly earners move into Millionaires' Row every Monday at 9:00 UTC."],
    [Swords, "Duels", "1v1", "rose", "Harbour Rush duels are 1v1 for stakes."],
    [Landmark, "Mayor", "Every 2d", "gold", "A mayor is elected every 2 days."],
  ];
  return (
    <Section id="how" title="How it works" accent="gold" info="Rent your berths to ships, collect UXD (late ships pay half), trade with other docks for boosts and mystery crates, rent a house uptown, put up billboards, reach Millionaires' Row and run for mayor.">
      <div className="grid grid-cols-3 gap-2">
        {tiles.map(([Icon, label, chip, acc, tip]) => (
          <div key={label} title={tip} className="hud-well flex flex-col items-center gap-1 rounded-[10px] p-2.5 text-center" style={{ "--acc": ACC[acc] }}>
            <Icon className="acc-text h-5 w-5" strokeWidth={2} />
            <b className="text-xs font-semibold leading-tight">{label}</b>
            <span className="text-[11px] tabular-nums text-x-mute">{chip}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Drawer({ state, onClose, onTab, town, city, me, user, eco, onCollect, onTrade, onFly, onLot, onShare, onFireworks, onSignIn }) {
  const phone = useMedia("(max-width: 639px)");
  const calm = useReducedMotion();
  const drag = useDragControls();
  const body = useRef();
  const { econ } = eco;

  useEffect(() => {
    const el = state?.section && body.current?.querySelector(`[data-section="${state.section}"]`);
    body.current?.scrollTo({ top: el ? el.offsetTop - body.current.offsetTop : 0, behavior: el && !calm ? "smooth" : "auto" });
  }, [state, calm]);

  const wait = user ? <Loading /> : <Prompt onSignIn={onSignIn} />;
  const slide = phone ? { y: "100%" } : { x: "110%" };
  return (
    <AnimatePresence>
      {state && phone && <motion.div key="shade" className="fixed inset-0 z-30 bg-[var(--x-scrim)]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />}
      {state && (
        <motion.aside key="drawer" role="dialog" aria-label="Menu" data-testid="drawer"
          initial={slide} animate={{ x: 0, y: 0 }} exit={slide} transition={calm ? { duration: 0 } : { type: "spring", damping: 34, stiffness: 340 }}
          drag={phone ? "y" : false} dragControls={drag} dragListener={false} dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.7 }}
          onDragEnd={(_, i) => (i.offset.y > 100 || i.velocity.y > 600) && onClose()}
          className={`${PANEL} hud-thick pointer-events-auto fixed z-30 flex flex-col overflow-hidden ${phone ? "inset-x-0 bottom-0 h-[85vh] max-h-[85vh] rounded-t-[28px]" : "bottom-12 right-3 top-[72px] w-[400px] rounded-[24px]"}`}>
          <div className="shrink-0 border-b border-x-line">
            {phone && (
              <div onPointerDown={(e) => drag.start(e)} className="flex cursor-grab touch-none justify-center pb-1 pt-2.5" data-testid="drawer-handle">
                <span className="h-1.5 w-10 rounded-full bg-x-active" />
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <div className="hud-well flex flex-1 gap-0.5 rounded-full p-0.5" role="tablist">
                {TABS.map(([id, label, Icon]) => (
                  <button key={id} type="button" role="tab" aria-selected={state.tab === id} onClick={() => onTab(id)} data-testid={`drawer-tab-${id}`}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-semibold transition-colors ${state.tab === id ? "bg-x-surface text-x-ink" : "text-x-mute hover:text-x-ink"}`}>
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />{label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" data-testid="drawer-close" className="hud-well grid h-8 w-8 place-items-center rounded-full text-x-mute transition-colors hover:bg-x-active hover:text-x-ink"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div ref={body} className="hud-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {state.tab === "economy" && (econ ? <EconomyTab econ={econ} eco={eco} challenge={state.challenge} onCollect={onCollect} onFly={onFly} onLot={onLot} onShare={onShare} onFireworks={onFireworks} /> : wait)}
            {state.tab === "shop" && (econ ? <ShopTab econ={econ} eco={eco} onCollect={onCollect} onFireworks={onFireworks} /> : wait)}
            {state.tab === "leaders" && <Leaderboard town={town} user={user} econ={econ} initial={state.section} onFly={onFly} onTrade={onTrade} />}
            {state.tab === "hall" && (
              <>
                {econ ? <HallPanel econ={econ} me={me} onChanged={eco.refresh} onVisit={onFly} /> : wait}
                <How econ={econ} />
              </>
            )}
            {state.tab === "place" && <div className="p-3 [&>div]:!w-full"><PlaceCard id={state.id} town={town} city={city} me={me} eco={eco} onVisit={onFly} onSignIn={onSignIn} onClose={onClose} /></div>}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
